import { DAPIOptions, DAPICorrelation } from "@/lib/dapi/types";
import getDapiRandomId from "@/lib/dapi/utils/getDapiRandomId";
import makeEvent from "@/hooks/eventing/makeEvent";
import { EVENTS } from "@/hooks/eventing/types/events";
import { getSessionId } from "@/utils/tagular/helpers";
import { useCallback, useEffect, useRef } from "react";

export const useDAPI = (options: DAPIOptions = {}) => {
  const { enabled = true } = options;

  const dapiRandomIdRef = useRef<string | null>(null);
  const webContextSessionIdRef = useRef<string | null>(null);
  const isEventFiredRef = useRef(false);

  // Initialize with stored values
  useEffect(() => {
    if (!enabled) return;

    const storedId = getDapiRandomId();
    dapiRandomIdRef.current = storedId || null;

    const sessionId = getSessionId();
    webContextSessionIdRef.current = sessionId || null;
  }, [enabled]);

  // Fire DAPI event
  const fireDAPIEvent = useCallback(() => {
    if (!enabled || isEventFiredRef.current) return;

    if (!dapiRandomIdRef.current) {
      console.error(
        "[DAPI Error] Error: Cannot fire DAPI event - No randomId found in cookie",
      );
      return;
    }

    const dapiData = {
      dapiRandomId: dapiRandomIdRef.current,
    };

    try {
      const dapiEvent = makeEvent<DAPICorrelation, EVENTS>(dapiData)(
        EVENTS.DAPICorrelation,
      );
      dapiEvent();
      isEventFiredRef.current = true;
    } catch (error) {
      console.error("[DAPI Error] Error firing DAPI event:", error);
    }
  }, [enabled]);

  return {
    fireDAPIEvent,
    dapiRandomId: dapiRandomIdRef.current,
    webContextSessionId: webContextSessionIdRef.current,
    isEventFired: isEventFiredRef.current,
  };
};
